import React, { CSSProperties } from "react";
import TextInputs from "./inputs/text_input";
import TextArea from "./inputs/text_area_input";
import Select from "./inputs/select_inputs";
import MultiSelect from "./inputs/multi_select_input";
import RadioGroup from "./inputs/radio_input";
import CheckboxGroup from "./inputs/check_box_input";
import ImageUpload from "./inputs/image_input";
import FileUpload from "./inputs/file_input";
import NumberInput from "./inputs/number_input";
import {
    MultiSelectValidationRules,
    NumberValidationRule,
    RadioValidationRule,
    SelectValidationRules,
    TextAreaValidationRules,
    TextInputValidationRules,
} from "types";

type InputType =
    | "text"
    | "textarea"
    | "select"
    | "multiselect"
    | "radio"
    | "checkbox"
    | "image"
    | "file"
    | "number";

interface BaseInputConfig {
    type: InputType;
    label: string;
    name: string;
}

interface TextInputConfig extends BaseInputConfig {
    type: "text";
    value: string;
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    error?: string;
    rules?: TextInputValidationRules;
}

interface TextAreaConfig extends BaseInputConfig {
    type: "textarea";
    value: string;
    label: string;
    placeholder?: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    error?: string;
    rules?: TextAreaValidationRules;
    style?: CSSProperties;
}

interface SelectInputConfig extends BaseInputConfig {
    type: "select";
    value: string;
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    options: { label: string; value: string }[];
    placeholder?: string;
    error?: string;
    rules: SelectValidationRules;
}

interface MultiSelectInputConfig extends BaseInputConfig {
    type: "multiselect";
    values: string[];
    onChange: (values: string[]) => void;
    options: { label: string; value: string }[];
    placeholder?: string;
    error?: string;
    rule?: MultiSelectValidationRules;
}

interface RadioInputConfig extends BaseInputConfig {
    type: "radio";
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    options: { label: string; value: string }[];
    error?: string;
    rules?: RadioValidationRule;
}

interface CheckboxInputConfig extends BaseInputConfig {
    type: "checkbox";
    values: string[];
    onChange: (values: string[]) => void;
    options: { label: string; value: string }[];
    error?: string;
}

interface ImageInputConfig extends BaseInputConfig {
    type: "image";
    onChange: (file: File | null) => void;
    error?: string;
}

interface FileInputConfig extends BaseInputConfig {
    type: "file";
    onChange: (file: any) => void;
    accept?: string;
    error?: string;
}

interface NumberInputConfig extends BaseInputConfig {
    type: "number";
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    min?: string;
    max?: string;
    step?: number;
    error?: string;
    rules?: NumberValidationRule;
}

type InputConfig =
    | TextInputConfig
    | TextAreaConfig
    | SelectInputConfig
    | MultiSelectInputConfig
    | RadioInputConfig
    | CheckboxInputConfig
    | ImageInputConfig
    | FileInputConfig
    | NumberInputConfig;

interface DynamicFormInputProps {
    inputs: InputConfig[];
}

const DynamicFormInput: React.FC<DynamicFormInputProps> = ({ inputs }) => {
    return (
        <>
            {inputs.map((input) => {
                switch (input.type) {
                    case "text":
                        return <TextInputs key={input.name} {...input} />;
                    case "textarea":
                        return <TextArea key={input.name} {...input} />;
                    case "select":
                        return <Select key={input.name} {...input} />;
                    case "multiselect":
                        return <MultiSelect key={input.name} {...input} />;
                    case "radio":
                        return <RadioGroup key={input.name} {...input} />;
                    case "checkbox":
                        return <CheckboxGroup key={input.name} {...input} />;
                    case "image":
                        return <ImageUpload key={input.name} {...input} />;
                    case "file":
                        return <FileUpload key={input.name} {...input} />;
                    case "number":
                        return <NumberInput key={input.name} {...input} />;
                    default:
                        return null;
                }
            })}
        </>
    );
};

export default DynamicFormInput;

